import { deriveGraph, type FlowAction } from '@agavi/blocks';

import type { Task } from '@/types/experience';

/**
 * What the last submission's outcome means for the Continue button.
 *
 * Separated from the page because the interesting part is not the navigation, it is the three
 * ways "there is nowhere to go" differ, and each of them has to produce a different button:
 *
 *   - **stay** — authored. The student is meant to try again, so there is no Continue at all.
 *   - **goto with no target** — a LOOSE THREAD. The teacher set a redirect and has not said where
 *     it goes. Null, so the button stays disabled: the Flow panel already flags this, and a
 *     button that guessed would be inventing a destination the teacher never chose.
 *   - **next at the end of the line** — `nextOf` is null on the last task of a spine or at the
 *     edge of a pathway, and there "next" genuinely IS the end. That is a finish, not a fault.
 *
 * `next` resolves through `deriveGraph`'s `nextOf` rather than by looking at array order, because
 * the rule is subtle (a pathway bounds the fallthrough, a detour lane splits at each head) and
 * that field exists to be its one definition.
 */
export type PreviewRoute = { kind: 'end' } | { kind: 'goto'; taskId: string };

/**
 * Whether the task is FINISHED with the student — the other question the bottom bar asks.
 *
 * It lives beside `previewRouteFor` because the two were the bug: one read the recorded outcome
 * and the other recomputed from the block verdicts on screen (`incorrect === 0 && neutral === 0`),
 * so a task could have a destination the UI refused to offer. A split-pathway choice fires a
 * neutral by construction, and the bar sat on Submit while the flow had already chosen a goto.
 *
 * The owner's rule: *"exit of the page is dependent only on what the flow event says, regardless
 * of the state of the blocks."* A `stay` keeps them; anything else is the task letting go.
 *
 * NOT THE SAME QUESTION AS `previewRouteFor`, which is why this is a second function rather than
 * a null check on the first. A `goto` with no target is a loose thread: the flow decided to leave
 * and the teacher never said where, so the task IS finished and there is still nowhere to go.
 * The bar needs both facts to show a Continue that is disabled rather than a Submit that lies.
 */
export function previewTaskFinished(outcome: FlowAction | undefined): boolean {
  return !!outcome && outcome.type !== 'stay';
}

export function previewRouteFor(
  outcome: FlowAction | undefined,
  taskId: string,
  tasks: Task[],
): PreviewRoute | null {
  if (!outcome || outcome.type === 'stay') return null;
  if (outcome.type === 'end') return { kind: 'end' };

  if (outcome.type === 'goto') {
    return outcome.targetTaskId ? { kind: 'goto', taskId: outcome.targetTaskId } : null;
  }

  const next = deriveGraph(tasks).nextOf[taskId] ?? null;
  return next ? { kind: 'goto', taskId: next } : { kind: 'end' };
}
